import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { Switch } from "@headlessui/react";
import AutoChar from "@/assets/altochar.png";
import AutoCharGrey from '@/assets/altocharGrey.png'
import { cn } from "@/lib/utils";
import { CircleUser, Clock, PowerOff, Smile, ThumbsUp, Leaf, Flame, ChevronDown } from "lucide-react";
import { setAutopilotStatus, setAutopilotGear } from "@/api/autopilot";
import { AutoPilotStatus } from "@/types";
import { App, Dropdown, Tooltip } from "antd";
import type { MenuProps } from "antd";

interface MonthlyData {
  consumption: number;
  savings: number;
  savingsPercent: number;
}

interface AutoPilotProps {
  id: string
  savedPower?: number
  consumption?: number
  status: AutoPilotStatus
  user?: string
  event?: string
  stage?: number
  lastUpdated?: string
  isShowEvent?: boolean
  isMonthlyReport?: boolean
  currentMonth?: MonthlyData
  lastMonth?: MonthlyData
  showGear?: boolean
  comfortState?: string
  startDate?: string
  currentGear?: {
    id: number
    gear: string
    config: {
      pmv_offset: number
      description: string
    }
  }
}

export function AutopilotCard({
  id,
  savedPower,
  consumption,
  status,
  user,
  lastUpdated,
  isShowEvent,
  stage,
  event,
  isMonthlyReport,
  currentMonth,
  lastMonth,
  showGear,
  comfortState,
  startDate,
  currentGear
}: AutoPilotProps) {
  const { message } = App.useApp();

  // Function to capitalize the first letter
  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

  const [time, setTime] = useState(lastUpdated ? dayjs(lastUpdated).format('YYYY-MM-DD HH:mm') : 'N/A');
  const [isEnabled, setIsEnabled] = useState(status === 'active')
  const [currentComfortState, setCurrentComfortState] = useState(currentGear?.gear ? capitalize(currentGear.gear) : "Balance");

  useEffect(() => {
    setIsEnabled(status === 'active')
  }, [status])

  useEffect(() => {
    setTime(lastUpdated ? dayjs(lastUpdated).format('YYYY-MM-DD HH:mm') : 'N/A');
  }, [lastUpdated])

  useEffect(() => {
    if (currentGear?.gear) {
      setCurrentComfortState(capitalize(currentGear.gear));
    } else if (comfortState) {
      setCurrentComfortState(comfortState);
    }
  }, [comfortState, currentGear]);

  const handleAutopilotChange = async (enabled: boolean) => {
    setIsEnabled(enabled);
    try {
      await setAutopilotStatus(id, enabled ? 'active' : 'inactive');
      message.open({
        type: 'success',
        content: 'Update Successfully',
      });
    } catch (e: unknown) {
      const error = e as Error;
      message.open({
        type: 'error',
        content: error.message,
      });
    }
  };

  const handleComfortChange = async (comfort: string) => {
    setCurrentComfortState(comfort);

    try {
      // Map the comfort level name to the corresponding ID
      const gearIdMap: Record<string, number> = {
        'Comfort': 1,
        'Balance': 2,
        'Economy': 3,
        'Ultra': 4
      };

      const gearId = gearIdMap[comfort];

      if (gearId) {
        await setAutopilotGear(id, gearId);
        message.open({
          type: 'success',
          content: `Comfort mode updated to ${comfort}`,
        });
      } else {
        throw new Error(`Invalid comfort level: ${comfort}`);
      }
    } catch (e: unknown) {
      const error = e as Error;
      message.open({
        type: 'error',
        content: error.message,
      });
    }
  };

  // Define comfort level options with their icons and descriptions
  const comfortItems: MenuProps['items'] = [
    {
      key: 'Comfort',
      label: (
        <Tooltip title="PMV Offset = 0: Energy saving while maintaining normal comfort levels" placement="right">
          <div className="flex items-center gap-2 w-full" onClick={() => handleComfortChange('Comfort')}>
            <Smile className="w-4 h-4 text-[#4096ff]" />
            <span className="text-[#444]">Comfort</span>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'Balance',
      label: (
        <Tooltip title="PMV Offset = 0.5: More energy saving with acceptable comfort levels" placement="right">
          <div className="flex items-center gap-2 w-full" onClick={() => handleComfortChange('Balance')}>
            <ThumbsUp className="w-4 h-4 text-[#4096ff]" />
            <span className="text-[#444]">Balance</span>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'Economy',
      label: (
        <Tooltip title="PMV Offset = 1.0: High energy saving but may feel warmer" placement="right">
          <div className="flex items-center gap-2 w-full" onClick={() => handleComfortChange('Economy')}>
            <Leaf className="w-4 h-4 text-[#4096ff]" />
            <span className="text-[#444]">Economy</span>
          </div>
        </Tooltip>
      ),
    },
    {
      key: 'Ultra',
      label: (
        <Tooltip title="PMV Offset = 1.5: Maximum energy saving with noticeably warmer environment" placement="right">
          <div className="flex items-center gap-2 w-full" onClick={() => handleComfortChange('Ultra')}>
            <Flame className="w-4 h-4 text-[#4096ff]" />
            <span className="text-[#444]">Ultra</span>
          </div>
        </Tooltip>
      ),
    },
  ];

  // Get icon based on comfort state
  const getComfortIcon = () => {
    switch (currentComfortState) {
      case 'Comfort': return <Smile className="w-[11px] h-[11px] text-white" />;
      case 'Balance': return <ThumbsUp className="w-[11px] h-[11px] text-white" />;
      case 'Economy': return <Leaf className="w-[11px] h-[11px] text-white" />;
      case 'Ultra': return <Flame className="w-[11px] h-[11px] text-white" />;
      default: return <ThumbsUp className="w-[11px] h-[11px] text-white" />;
    }
  };

  // Get background gradient based on comfort state
  const getComfortGradient = () => {
    if (!showGear) return "";

    switch (currentComfortState) {
      case 'Comfort':
        return isEnabled
          ? "bg-gradient-to-br from-[#0E7EE4] to-[#55A6F2] border-[#55A6F2]"
          : "bg-gradient-to-br from-[#0E7EE4]/70 to-[#55A6F2]/70 border-[#C0E2FF] opacity-85";
      case 'Balance':
        return isEnabled
          ? "bg-gradient-to-br from-[#14B8B4] to-[#59D1CE] border-[#14B0BC]"
          : "bg-gradient-to-br from-[#14B8B4]/70 to-[#59D1CE]/70 border-[#C0E2FF] opacity-85";
      case 'Economy':
        return isEnabled
          ? "bg-gradient-to-br from-[#43A452] to-[#79B682] border-[#43A452]"
          : "bg-gradient-to-br from-[#43A452]/70 to-[#79B682]/70 border-[#C0E2FF] opacity-85";
      case 'Ultra':
        return isEnabled
          ? "bg-gradient-to-br from-[#F3724B] to-[#EE1376] border-[rgba(234,102,63,0.2)]"
          : "bg-gradient-to-br from-[#F3724B]/70 to-[#EE1376]/70 border-[#C0E2FF] opacity-85";
      default:
        return isEnabled
          ? "bg-gradient-to-br from-[#0E7EE4] to-[#55A6F2] border-[#55A6F2]"
          : "bg-gradient-to-br from-[#0E7EE4]/70 to-[#55A6F2]/70 border-[#C0E2FF] opacity-85";
    }
  };

  // Get button background color for the comfort dropdown
  const getDropdownButtonBg = () => {
    switch (currentComfortState) {
      case 'Comfort': return isEnabled ? "bg-[#0E7EE4]" : "bg-[#0E7EE4]/80";
      case 'Balance': return isEnabled ? "bg-[#14B8B4]" : "bg-[#14B8B4]/80";
      case 'Economy': return isEnabled ? "bg-[#43A452]" : "bg-[#43A452]/80";
      case 'Ultra': return isEnabled ? "bg-[#F3724B]" : "bg-[#F3724B]/80";
      default: return isEnabled ? "bg-[#0E7EE4]" : "bg-[#0E7EE4]/80";
    }
  };

  // Get the darker shade for icon background and other elements based on comfort state
  const getDarkerShade = () => {
    switch (currentComfortState) {
      case 'Comfort': return isEnabled ? "bg-[#065BA9]" : "bg-[#065BA9]/80";
      case 'Balance': return isEnabled ? "bg-[#0E9994]" : "bg-[#0E9994]/80";
      case 'Economy': return isEnabled ? "bg-[#32813E]" : "bg-[#32813E]/80";
      case 'Ultra': return isEnabled ? "bg-[#D54E2E]" : "bg-[#D54E2E]/80";
      default: return isEnabled ? "bg-[#065BA9]" : "bg-[#065BA9]/80";
    }
  };

  // Get background color for badge elements (slightly transparent version of the color)
  const getBadgeBg = () => {
    switch (currentComfortState) {
      case 'Comfort': return isEnabled ? "bg-[rgba(139,198,255,0.3)]" : "bg-[rgba(139,198,255,0.25)]";
      case 'Balance': return isEnabled ? "bg-[rgba(89,209,206,0.3)]" : "bg-[rgba(89,209,206,0.25)]";
      case 'Economy': return isEnabled ? "bg-[rgba(121,182,130,0.3)]" : "bg-[rgba(121,182,130,0.25)]";
      case 'Ultra': return isEnabled ? "bg-[rgba(243,114,75,0.3)]" : "bg-[rgba(243,114,75,0.25)]";
      default: return isEnabled ? "bg-[rgba(139,198,255,0.3)]" : "bg-[rgba(139,198,255,0.25)]";
    }
  };

  // Determine if we should use the gradient background
  const useGradientBackground = showGear || isMonthlyReport;
  // Get the appropriate gradient based on comfort state if showGear is true
  const gradientClasses = useGradientBackground
    ? (showGear ? getComfortGradient() : isEnabled
      ? "bg-gradient-to-br from-[#0E7EE4] to-[#55A6F2] border-[#55A6F2]"
      : "bg-gradient-to-br from-[#0E7EE4]/70 to-[#55A6F2]/70 border-[#C0E2FF] opacity-85")
    : "";

  return (
    <div
      className={cn(
        `w-full min-w-[286px] p-[6px] px-[8px] gap-[9px] transition duration-800 linear
        rounded-[12px] border backdrop-blur-[20px] shadow-popup
        inline-flex flex-col justify-center items-center`,
        useGradientBackground
          ? gradientClasses + (isEnabled ? " text-white" : " text-white/90")
          : isEnabled
            ? "border-mid-blue bg-alto-blue text-white"
            : "border-[#C0E2FF] bg-light text-[#B4B4B4]"
      )}
    >

      <div className="w-full flex justify-between items-center">
        <div className="gap-[6px] flex justify-start items-center">
          <div className={cn("w-[24px] h-[24px] rounded-[20px] pt-[4px] pl-[1px] overflow-hidden",
            useGradientBackground
              ? (showGear ? getDarkerShade() : isEnabled ? "bg-[#065BA9]" : "bg-[#065BA9]/80")
              : isEnabled ? "bg-dark-blue" : "bg-white-01"
          )}>
            <img
              src={isEnabled ? AutoChar : AutoCharGrey}
              alt="Autopilot"
              className={cn("w-[22px] h-[22px] object-cover", !isEnabled && "opacity-85")}
            />
          </div>
          <p className={cn("heading-10",
            useGradientBackground
              ? (isEnabled ? "text-white" : "text-white/90")
              : isEnabled ? "text-white" : "text-[#B4B4B4]"
          )}>
            Autopilot
          </p>
        </div>
        <div className="gap-[9px] flex justify-start items-center">
          {showGear && (
            <Dropdown menu={{ items: comfortItems }} placement="bottomRight" trigger={['click']}>
              <Tooltip title="Click to adjust comfort settings" placement="top">
                <div className={cn("px-[8px] py-[4px] rounded-[6px] flex items-center gap-[4px] cursor-pointer hover:opacity-90 transition-colors",
                  getDropdownButtonBg(),
                  !isEnabled && "opacity-85"
                )}>
                  <div className="flex items-center justify-center">
                    {getComfortIcon()}
                  </div>
                  <span className={cn("text-white text-[10px] font-medium leading-none pt-[1px]", !isEnabled && "text-white/90")}>
                    {currentComfortState}
                  </span>
                  <ChevronDown className={cn("w-[10px] h-[10px] text-white", !isEnabled && "text-white/90")} />
                </div>
              </Tooltip>
            </Dropdown>
          )}
          <div className="gap-[4px] flex justify-start items-center">
            <p className={cn("paragraph-10", !isEnabled && useGradientBackground && "text-white/90")}>
              {isEnabled ? 'Active' : 'Inactive'}
            </p>
            <Switch
              checked={isEnabled}
              onChange={handleAutopilotChange}
              className={cn(
                "relative inline-flex h-4 w-8 items-center rounded-full",
                useGradientBackground
                  ? (showGear ? getDarkerShade() : isEnabled ? "bg-[#065BA9]" : "bg-[#065BA9]/80")
                  : isEnabled ? "bg-dark-blue" : "bg-white-01"
              )}
            >
              <span className="sr-only">Power</span>
              <span
                className={cn(
                  "inline-block h-3 w-3 transform rounded-full bg-white transition",
                  isEnabled ? "translate-x-4" : "translate-x-1"
                )}
              />
            </Switch>
          </div>
        </div>
      </div>

      {isMonthlyReport && (
        <>
          <div className="w-full">
            <div className={cn("inline-block rounded-[4px] mt-0 px-1.5 py-[1.5px]",
              showGear ? getBadgeBg() : isEnabled ? "bg-[rgba(139,198,255,0.3)]" : "bg-[rgba(139,198,255,0.25)]"
            )}>
              <p className={cn("text-white text-[8px] font-normal", !isEnabled && "text-white/90")}>
                All time: since {startDate || "N/A"}
              </p>
            </div>
            <div className="w-full flex justify-between items-start relative h-[62px] overflow">
              <div className="pt-[10px] pb-[4px] flex flex-col items-start z-10">
                <div className={cn("text-white text-[25px] font-semibold tracking-[0.01em] leading-[1.1]", !isEnabled && "text-white/90")}>
                  {savedPower?.toLocaleString() || "0"}
                </div>
                <div className={cn("text-white text-[11px] font-normal mt-[1px]", !isEnabled && "text-white/90")}>
                  kWh saved
                </div>
              </div>

              <div
                data-font="barlow-condensed"
                style={{
                  letterSpacing: '-0.02em',
                  fontStretch: 'condensed',
                  fontWeight: 600
                }}
                className={cn("text-[rgba(255,255,255,0.1)] text-[70px] absolute right-[5px] top-[-10px] select-none pointer-events-none z-0 leading-[0.9]",
                  !isEnabled && "text-[rgba(255,255,255,0.08)]"
                )}>
                {currentMonth?.savingsPercent?.toFixed(1) || "N/A"}<span style={{ fontSize: '60%', verticalAlign: 'top', marginLeft: '-1px' }}>%</span>
              </div>
            </div>
          </div>

          <div className="w-full flex gap-[6px] mt-[-8px] mb-0.5">
            {/* Current Month */}
            <div className={cn("flex-1 h-[74px] p-[5px] px-[8px] rounded-[7px] flex flex-col justify-between",
              showGear ? getBadgeBg() : isEnabled ? "bg-[rgba(139,198,255,0.5)]" : "bg-[rgba(139,198,255,0.4)]")}>
              <div className={cn("text-[#F0F0F0] text-[8px] font-bold", !isEnabled && "text-[#F0F0F0]/90")}>This Month</div>
              <div className="flex justify-between items-center">
                <div className={cn("text-[#F0F0F0] text-[8px]", !isEnabled && "text-[#F0F0F0]/90")}>Consumption</div>
                <div className={cn("text-white text-[9px] font-bold", !isEnabled && "text-white/90")}>
                  {currentMonth?.consumption?.toLocaleString() || "N/A"} kWh
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className={cn("text-[#F0F0F0] text-[8px]", !isEnabled && "text-[#F0F0F0]/90")}>Savings</div>
                <div className={cn("text-white text-[9px] font-bold", !isEnabled && "text-white/90")}>
                  {currentMonth?.savings?.toLocaleString() || "N/A"} kWh
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className={cn("text-[#F0F0F0] text-[8px]", !isEnabled && "text-[#F0F0F0]/90")}>Savings %</div>
                <div className={cn("text-white text-[9px] font-bold", !isEnabled && "text-white/90")}>
                  {currentMonth?.savingsPercent?.toFixed(1) || "N/A"} %
                </div>
              </div>
            </div>

            {/* Last Month */}
            <div className={cn("flex-1 h-[74px] p-[5px] px-[8px] rounded-[7px] flex flex-col justify-between",
              showGear ? getBadgeBg() : isEnabled ? "bg-[rgba(139,198,255,0.5)]" : "bg-[rgba(139,198,255,0.4)]")}>
              <div className={cn("text-[#F0F0F0] text-[8px] font-bold", !isEnabled && "text-[#F0F0F0]/90")}>Last Month</div>
              <div className="flex justify-between items-center">
                <div className={cn("text-[#F0F0F0] text-[8px]", !isEnabled && "text-[#F0F0F0]/90")}>Consumption</div>
                <div className={cn("text-white text-[9px] font-bold", !isEnabled && "text-white/90")}>
                  {lastMonth?.consumption?.toLocaleString() || "N/A"} kWh
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className={cn("text-[#F0F0F0] text-[8px]", !isEnabled && "text-[#F0F0F0]/90")}>Savings</div>
                <div className={cn("text-white text-[9px] font-bold", !isEnabled && "text-white/90")}>
                  {lastMonth?.savings?.toLocaleString() || "N/A"} kWh
                </div>
              </div>
              <div className="flex justify-between items-center">
                <div className={cn("text-[#F0F0F0] text-[8px]", !isEnabled && "text-[#F0F0F0]/90")}>Savings %</div>
                <div className={cn("text-white text-[9px] font-bold", !isEnabled && "text-white/90")}>
                  {lastMonth?.savingsPercent?.toFixed(1) || "N/A"} %
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {consumption && !isMonthlyReport && (
        <div className="w-full">
          <div>
            <div className="gap-[12px] flex flex-row items-baseline">
              <p className={cn("heading-2", !isEnabled && useGradientBackground && "text-white/90")}>
                {savedPower?.toLocaleString()}
              </p>
              <p className={cn("paragraph-12", !isEnabled && useGradientBackground && "text-white/90")}>
                kWh saved
              </p>
            </div>
            <div className="gap-[4px] flex flex-row items-baseline">
              <p className={cn("paragraph-8", !isEnabled && useGradientBackground && "text-white/90")}>
                Consumption
              </p>
              <p className={cn("paragraph-8", !isEnabled && useGradientBackground && "text-white/90")}>
                <span className="font-semibold">{consumption?.toLocaleString()}</span> kWh
              </p>
            </div>
          </div>
        </div>
      )}

      {isShowEvent && !isMonthlyReport ?
        <div className="flex flex-col w-full gap-0.5">
          <p className={cn("heading-8", !isEnabled && useGradientBackground && "text-white/90")}>
            Current
          </p>
          <div className="w-full gap-1 grid grid-cols-2 items-center">
            <div className={cn("flex grow rounded-[6px] flex flex-row justify-between items-center py-0.5 px-[5px] min-h-[22px]",
              showGear ? getBadgeBg() : isEnabled ? "bg-heatmap-cool" : "bg-heatmap-cool/80")}>
              <p className={cn("paragraph-9", !isEnabled && useGradientBackground && "text-white/90")}>
                Event
              </p>
              <div className="gap-[2px] flex flex-row items-center">
                <PowerOff className={cn("h-[9px] w-[9px]", !isEnabled && useGradientBackground && "text-white/90")} />
                <p className={cn("paragraph-9", !isEnabled && useGradientBackground && "text-white/90")}>
                  {event}
                </p>
              </div>
            </div>
            <div className={cn("flex grow gap-[4px] rounded-[6px] flex flex-row justify-between items-center py-0.5 px-[5px] min-h-[22px]",
              showGear ? getBadgeBg() : isEnabled ? "bg-heatmap-cool" : "bg-heatmap-cool/80")}>
              <p className={cn("paragraph-9", !isEnabled && useGradientBackground && "text-white/90")}>
                Stage
              </p>
              <p className={cn("paragraph-9", !isEnabled && useGradientBackground && "text-white/90")}>
                {stage}
              </p>
            </div>
          </div>
        </div>
        : (!isMonthlyReport &&
          <div className="w-full gap-[4px] flex flex-row items-center">
            <div className={cn("flex-grow rounded-[6px] flex flex-row justify-between items-center pt-[2px] pl-[5px] pb-[2px] pr-[5px]",
              showGear ? getBadgeBg() : isEnabled ? "bg-heatmap-cool" : "bg-heatmap-cool/80")}>
              <p className={cn("paragraph-9", !isEnabled && useGradientBackground && "text-white/90")}>
                Updated by
              </p>
              <div className="gap-[2px] flex flex-row items-center">
                <CircleUser className={cn("h-[9px] w-[9px]", !isEnabled && useGradientBackground && "text-white/90")} />
                <p className={cn("paragraph-9", !isEnabled && useGradientBackground && "text-white/90")}>
                  {user}
                </p>
              </div>
            </div>
            <div className={cn("gap-[4px] rounded-[6px] flex flex-row justify-between items-center pt-[2px] pl-[2px] pb-[2px] pr-[5px]",
              showGear ? getBadgeBg() : isEnabled ? "bg-heatmap-cool" : "bg-heatmap-cool/80")}>
              <Clock className={cn("h-[9px] w-[9px]", !isEnabled && useGradientBackground && "text-white/90")} />
              <p className={cn("paragraph-9", !isEnabled && useGradientBackground && "text-white/90")}>
                {time}
              </p>
            </div>
          </div>
        )}
    </div>
  );
}